import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiHeaders } from "@/hooks/use-api-headers";
import type { CantVerifyReason } from "@/lib/cant-verify-reasons";
import type { BoundingArea } from "@/lib/image-crop";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";
const BASE = `${API_URL}/api/v1/labelling`;

/**
 * Response shapes are declared here rather than generated: the pages in this
 * codebase hand-write their interfaces (`api.types.ts` has no consumer).
 *
 * Note what the item does *not* carry: the review pass. The server knows whether
 * this is a first opinion or a blind second one and deliberately does not say —
 * a labeller who knew would also know the first labeller had said "correct", and
 * the agreement score would measure agreeableness rather than accuracy.
 */
export type LabellingItem = {
  event_id: string;
  lock_expires_at: string;
  category: string | null;
  label: string | null;
  classification_level: number | null;
  waste_type: string | null;
  item_weight: string;
  before_image_read_url: string | null;
  image_read_url: string | null;
  logged_at: string;
  processed_at: string | null;
  cost_estimate: string | null;
  currency: string;
  site_name: string;
  kitchen_name: string;
  service_time: string | null;
  device_serial: string | null;
  // The device's crop box (POS2-1105) — both captures render cropped to it, so the
  // labeller judges the frame the classifier judged. Null keeps the frame whole.
  bounding_area: BoundingArea | null;
  timezone: string;
  is_heavy: boolean;
};

export type QueueCounts = {
  pending: number;
  heavy_pending: number;
  heavy_threshold_kg: string;
};

export type ShiftSummary = {
  shift_id: string;
  started_at: string;
  ended_at: string | null;
  end_reason: "manual" | "auto_idle" | null;
  duration_seconds: number;
  items_labelled: number;
  items_per_hour: number;
  target_items: number;
  correct_count: number;
  relabel_count: number;
  discard_count: number;
  cant_verify_count: number;
};

export type ShiftState = {
  shift: ShiftSummary | null;
  last_shift: ShiftSummary | null;
  queue: QueueCounts;
  can_undo: boolean;
};

export type LabellingActionInput = {
  event_id: string;
  action: "correct" | "relabel" | "discard" | "cant_verify";
  handling_time_ms: number;
  category_id?: string | null;
  subcategory_id?: string | null;
  inventory_item_id?: string | null;
  // Required for a can't-verify and rejected on anything else, which the server
  // enforces — a flag with no reason records that work was skipped and nothing
  // about why.
  cant_verify_reason?: CantVerifyReason;
};

export type CatalogueCategory = {
  id: string;
  name: string;
  parent_id: string | null;
  sort_order: number;
};

export type CatalogueItem = {
  id: string;
  description: string;
  simple_name: string | null;
  clean_name: string | null;
  // Carried so a search hit found outside the drill can still commit an L4 — a
  // relabel writes the whole triple. Nullable, as they are on the table.
  category_id: string | null;
  subcategory_id: string | null;
};

/**
 * An error that kept its status, because 409 means something specific here.
 *
 * Every conflict the workstation can hit says the same thing underneath: the item you
 * were holding is no longer yours to judge. That is recoverable — take the next one —
 * whereas a network failure or a 500 is not, and must leave the held item alone. The
 * two are indistinguishable from a message alone.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export const isConflict = (error: unknown) => error instanceof ApiError && error.status === 409;

async function readJson<T>(res: Response, what: string): Promise<T> {
  if (!res.ok) {
    // The backend returns 409 with a readable `detail` for every state the workstation
    // has to explain rather than swallow: off shift, a lock that expired while the
    // labeller was looking at the card, an event a chef changed underneath them, and an
    // item that already carries a verdict for this pass.
    const detail = await res
      .json()
      .then((body) => body?.detail as string | undefined)
      .catch(() => undefined);
    throw new ApiError(detail ?? `Failed to ${what}: ${res.status}`, res.status);
  }
  return (await res.json()).data as T;
}

const SHIFT_KEY = ["labelling", "shift"];
const ITEM_KEY = ["labelling", "item"];

export function useShiftState() {
  const headers = useApiHeaders();
  return useQuery<ShiftState>({
    queryKey: SHIFT_KEY,
    queryFn: async () =>
      readJson<ShiftState>(await fetch(`${BASE}/shift`, { headers }), "load shift state"),
    // The idle auto clock-out is evaluated on this read, so polling is also what
    // closes a shift the labeller walked away from.
    refetchInterval: 60_000,
  });
}

export function useStartShift() {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      readJson(await fetch(`${BASE}/shift/start`, { method: "POST", headers }), "start shift"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SHIFT_KEY });
      queryClient.invalidateQueries({ queryKey: ITEM_KEY });
    },
  });
}

export function useEndShift() {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      readJson(await fetch(`${BASE}/shift/end`, { method: "POST", headers }), "end shift"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SHIFT_KEY });
      queryClient.removeQueries({ queryKey: ITEM_KEY });
    },
  });
}

/**
 * Claims the next item. `null` is the queue-clear state, not an error.
 *
 * `enabled` is the on-shift flag: claiming while off shift is a 409 by design,
 * and firing it would be asking the server to refuse.
 */
export function useNextItem(enabled: boolean) {
  const headers = useApiHeaders();
  return useQuery<LabellingItem | null>({
    queryKey: ITEM_KEY,
    queryFn: async () =>
      readJson<LabellingItem | null>(await fetch(`${BASE}/next`, { headers }), "claim next item"),
    enabled,
    // Each claim takes a lock, so this must never fire on its own — only when
    // the previous item has been actioned or undone.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useSubmitAction() {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: LabellingActionInput) =>
      readJson(
        await fetch(`${BASE}/actions`, {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
        "submit action",
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SHIFT_KEY });
      queryClient.invalidateQueries({ queryKey: ITEM_KEY });
    },
    // A conflict means the item is no longer this shift's to judge — its lease ran out,
    // a chef changed it, or it already carries a verdict. Refetching claims the next one
    // straight away, which is the whole difference between a transition and a dead end:
    // before this, the card stayed on screen with live buttons, every retry failed
    // identically, and reloading the page was the only way on.
    //
    // Deliberately not on every error. A network blip or a 500 leaves the lock intact,
    // and refetching then would abandon a perfectly good held item.
    onError: (error) => {
      if (!isConflict(error)) return;
      queryClient.invalidateQueries({ queryKey: SHIFT_KEY });
      queryClient.invalidateQueries({ queryKey: ITEM_KEY });
    },
  });
}

export function useUndoLast() {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      readJson(
        await fetch(`${BASE}/actions/last`, { method: "DELETE", headers }),
        "undo last action",
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SHIFT_KEY });
      queryClient.invalidateQueries({ queryKey: ITEM_KEY });
    },
  });
}

export function useCatalogueCategories(enabled: boolean) {
  const headers = useApiHeaders();
  return useQuery<CatalogueCategory[]>({
    queryKey: ["labelling", "catalogue", "categories"],
    queryFn: async () =>
      readJson<CatalogueCategory[]>(
        await fetch(`${BASE}/catalogue/categories`, { headers }),
        "load categories",
      ),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * Catalogue items for the held event's own site. Scoped by event rather than by
 * list id — see the endpoint's comment: holding the lock is the authorisation.
 *
 * Two ways in, and exactly one is used at a time: a sub-category, which lists what
 * sits under it, or a search term, which spans the whole catalogue so an item can
 * be found without knowing where it lives. The caller passes one or the other.
 */
export function useCatalogueItems(
  eventId: string | null,
  subcategoryId: string | null,
  search: string,
) {
  const headers = useApiHeaders();
  return useQuery<CatalogueItem[]>({
    queryKey: ["labelling", "catalogue", "items", eventId, subcategoryId, search],
    queryFn: async () => {
      const params = new URLSearchParams({ event_id: eventId! });
      if (subcategoryId) params.set("subcategory_id", subcategoryId);
      if (search) params.set("search", search);
      return readJson<CatalogueItem[]>(
        await fetch(`${BASE}/catalogue/items?${params}`, { headers }),
        "load catalogue items",
      );
    },
    enabled: !!eventId && (!!subcategoryId || !!search),
  });
}
